import { useDynamicConfig as useStatsigDynamicConfig } from '@/src/services/Statsig/PlatformStatsig'
import { useStatsigLoadingStatus } from '@/src/services/Statsig'
import { PointsToCashForMortgage } from '../../experiences/points-to-cash-for-mortgage/statsig/dynamic-config/usePointsToCashForMortgageDynamicConfig'
import { PointsToStatementCredit } from '@/src/experiences/points-to-statement-credit/statsig/dynamic-config/usePointsToStatementCreditDynamicConfig'
import { OmniPointsTransfer } from '@/src/experiences/points-to-omni/statsig/dynamic-config/useOmniPointsTransferDynamicConfig'
import { RewardsControl } from '@/src/experiences/rewards/statsig/dynamic-config/useRewardsControlDynamicConfig'

type DynamicConfig = Record<string, unknown>

type DynamicConfigs = Record<string, DynamicConfig>

type DefineDynamicConfigs<T extends DynamicConfigs> = T

/** @see {@link https://console.statsig.com/1G3Xqca75leoEUUlzU7xDl/dynamic_configs} */
type MesaDynamicConfig = DefineDynamicConfigs<{
  mortgage_points_transfer: PointsToCashForMortgage
  statement_credit_: PointsToStatementCredit
  omni_points_transfer: OmniPointsTransfer
  rewards_control: RewardsControl
}>

export function useMesaDynamicConfig<ConfigName extends keyof MesaDynamicConfig>(
  ...props: [
    configName: ConfigName,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    ...args: Parameters<typeof useStatsigDynamicConfig> extends [string, ...infer Rest]
      ? Rest
      : never,
  ]
) {
  const loadingStatus = useStatsigLoadingStatus()

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const dynamicConfig = useStatsigDynamicConfig(...props)

  switch (loadingStatus) {
    case 'Ready':
      return {
        loadingStatus,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        dynamicConfig: dynamicConfig as Omit<typeof dynamicConfig, 'get' | 'value' | 'name'> & {
          get: <ConfigKey extends keyof MesaDynamicConfig[ConfigName], Fallback>(
            configKey: ConfigKey,
            fallback: Fallback,
          ) => MesaDynamicConfig[ConfigName][ConfigKey] | Fallback
          value: MesaDynamicConfig[ConfigName]
          name: ConfigName
        },
      } as const
    default:
      return { loadingStatus } as const
  }
}
